import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import {
  ListBox,
  Item,
  View,
  ProgressCircle,
  Text,
  Flex,
  ActionButton
} from '@adobe/react-spectrum';
import Checkmark from '@spectrum-icons/workflow/Checkmark';
import ChevronDown from '@spectrum-icons/workflow/ChevronDown';
import Close from '@spectrum-icons/workflow/Close';
import styles from './LDAPSearch.module.scss';
import { CHIP_REMOVE_DISABLED_TITLE_DEFAULT } from './ldapSearchConstants';
import Alert from '@spectrum-icons/workflow/Alert';
import { getCookie } from 'utils/helpers';
import { AlertBanner } from 'app/components/Modal/AlertBanner';
import BlackCheckMarkIcon from 'app/assets/svgIcons/BlackCheckMarkIcon';
import {
  excludeUsers,
  isExcludedUser,
  type UserIdentity
} from 'utils/excludeUser';

/** No search fires below this length — avoids firing broad, expensive queries
 * (and, in `users-and-groups` mode, two of them) on the first keystroke or two. */
const MIN_QUERY_LENGTH = 3;
/** Delay after the user stops typing before the search actually fires. Collapses
 * a burst of keystrokes into one request instead of one (or two, in
 * `users-and-groups` mode) per keystroke. */
const SEARCH_DEBOUNCE_MS = 300;

interface LDAPSearchFieldProps {
  label: string;
  value: any; // Single contact or null
  onChange: (value: any) => void;
  isRequired?: boolean;
  isInvalid?: boolean;
  ldapContacts?: any[];
  existingContacts?: any[]; // Add prop for existing DACI contacts
  currentDaciRole?: string; // Add prop for current DACI role to enable role-specific duplicate checking
  currentDriverRole?: string; // Add prop for current driver sub-role (for Driver role only)
  onDeleteExistingContact?: (contact: any) => void; // Add prop for deleting existing contacts via chip X
  /** When true, chip has no remove control, not-allowed cursor, and optional hover title. */
  isChipRemoveDisabled?: (contact: any) => boolean;
  chipRemoveDisabledTitle?: string;
  autoFocus?: boolean; // Add prop to auto-focus the input field
  searchMode?: 'users' | 'users-and-groups'; // Add prop for dual API search (users only or users + groups)
  /** People who must never be offered or selected (matched by uid, then email). */
  excludeContacts?: UserIdentity[];
  /** Excludes self and explains why when no selectable search results remain. */
  selfSelection?: {
    user?: UserIdentity | null;
    message: string;
  };
  cssClasses?: {
    ldapFieldContainer?: string;
    pickerContainer?: string;
    centeredPickerContainer?: string;
    ldapTextField?: string;
    centeredInput?: string;
    tagContainer?: string;
    selectedTag?: string;
    tagRemoveButton?: string;
    ldapDropdown?: string;
  };
}

const LDAPSearchField: React.FC<LDAPSearchFieldProps> = ({
  label,
  value,
  onChange,
  isRequired,
  isInvalid,
  cssClasses,
  existingContacts = [],
  currentDaciRole,
  currentDriverRole,
  onDeleteExistingContact,
  isChipRemoveDisabled,
  chipRemoveDisabledTitle = CHIP_REMOVE_DISABLED_TITLE_DEFAULT,
  autoFocus = false,
  searchMode = 'users', // Default to users-only search for backward compatibility
  excludeContacts,
  selfSelection
}) => {
  const [inputValue, setInputValue] = useState('');
  const [results, setResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [hasSelfMatch, setHasSelfMatch] = useState(false);

  // Track selected users as an array
  const [selectedUsers, setSelectedUsers] = useState<any[]>([]);

  const containerRef = useRef<HTMLDivElement>(null);
  const inputWrapperRef = useRef<HTMLDivElement>(null);
  const inputFieldRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchRequestIdRef = useRef(0);
  // Read via ref so callers can pass an inline array without re-firing the search.
  const excludeContactsRef = useRef(excludeContacts);
  excludeContactsRef.current = selfSelection?.user
    ? [...(excludeContacts || []), selfSelection.user]
    : excludeContacts;
  const selfSelectionRef = useRef(selfSelection);
  selfSelectionRef.current = selfSelection;
  const [error, setError] = useState<string | null>(null);
  // Caps the dropdown's height to whatever room is actually available below
  // the input when it sits inside a scrollable ancestor (e.g.
  // EditMilestoneModal's `.figma-modal-content.is-scrollable`, which only
  // appears once Send-for-Approval expands the form) — that ancestor's
  // `overflow-y: auto` would otherwise clip it. `undefined` = use the CSS
  // default (300px/400px), i.e. today's look, everywhere space isn't tight.
  const [dropdownMaxHeight, setDropdownMaxHeight] = useState<
    number | undefined
  >(undefined);

  // Add state for API errors
  const [apiError, setApiError] = useState<{
    message: string;
    type: 'auth' | 'network' | 'general';
  } | null>(null);

  // Initialize selected users from value prop
  useEffect(() => {
    if (value && value.uid) {
      // If value is a single object, convert to array with one item
      setSelectedUsers([value]);
    } else if (Array.isArray(value)) {
      // If value is already an array, use it directly
      setSelectedUsers(value);
    } else {
      // If no value, empty array
      setSelectedUsers([]);
    }
  }, [value]);

  // Auto-focus the input field when autoFocus is true
  useEffect(() => {
    if (autoFocus) {
      // Small delay to ensure DOM is ready
      const timer = setTimeout(() => {
        focusInput();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [autoFocus]);

  // Helper function to focus input
  const focusInput = () => {
    // Try to focus using traditional DOM method first
    const input =
      inputFieldRef.current ?? inputWrapperRef.current?.querySelector('input');
    if (input instanceof HTMLInputElement) {
      input.focus();
    }
  };

  const formatResultLabel = (user: any) => {
    const isGroup = user.sourceType === 'groups';
    if (isGroup) {
      return user.name || user.uid || '';
    }
    const uid = user.uid?.trim();
    return uid ? `${user.name} (${uid})` : user.name || uid || '';
  };

  const resultKey = (user: any, idx: number) =>
    `${user.sourceType || 'users'}-${
      user.uid || user.name || user.email || 'result'
    }-${idx}`;

  // Check if a user already exists for the CURRENT role (works for both DACI and Primary Contacts)
  const isUserAlreadyInRole = (user: any) => {
    // If no current role specified, allow all selections (backward compatibility)
    if (!currentDaciRole) {
      return false;
    }

    // For DACI Driver role, check both DACI role AND driver sub-role
    if (currentDaciRole === 'Driver' && currentDriverRole) {
      const isDuplicate = existingContacts.some(
        contact =>
          contact.daciRole === 'Driver' &&
          contact.role === currentDriverRole && // Check driver sub-role too
          (contact.uid === user.uid ||
            contact.name?.toLowerCase() === user.name?.toLowerCase())
      );

      return isDuplicate;
    }

    // For Primary Contact roles (PROD MRKT MANAGER, PRODUCT MANAGER, PROGRAM MANAGER)
    const primaryContactRoles = [
      'PROD MRKT MANAGER',
      'PRODUCT MANAGER',
      'PROGRAM MANAGER'
    ];
    if (primaryContactRoles.includes(currentDaciRole)) {
      const isDuplicate = existingContacts.some(
        contact =>
          contact.role === currentDaciRole && // Check exact role match for Primary Contacts
          (contact.uid === user.uid ||
            contact.name?.toLowerCase() === user.name?.toLowerCase())
      );

      return isDuplicate;
    }

    // For other DACI roles (Approver, Contributor, Informed), only check DACI role
    const isDuplicate = existingContacts.some(
      contact =>
        contact.daciRole === currentDaciRole && // Only check current DACI role
        (contact.uid === user.uid ||
          contact.name?.toLowerCase() === user.name?.toLowerCase())
    );

    return isDuplicate;
  };

  // Check if a user is currently selected in this session
  const isUserSelected = (user: any) => {
    return selectedUsers.some(
      selected =>
        (user.uid && selected.uid === user.uid) ||
        (user.name && selected.name?.toLowerCase() === user.name?.toLowerCase())
    );
  };
  // Keyboard navigation over results
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
  const resultsListRef = useRef<HTMLDivElement>(null);

  // Reset highlighted index whenever results change
  useEffect(() => {
    setHighlightedIndex(-1);
  }, [results]);

  // Re-measure whenever the dropdown becomes visible or its content changes
  // height (loading spinner -> results list -> no-results message all differ).
  // Deliberately keeps the dropdown always anchored BELOW the input (users
  // are used to it there) — instead of flipping direction when space is
  // tight, caps its own max-height to whatever room is actually available,
  // so it becomes internally scrollable rather than clipped or relocated.
  useLayoutEffect(() => {
    const isDropdownVisible =
      showResults &&
      (loading ||
        results.length > 0 ||
        (inputValue.trim().length >= MIN_QUERY_LENGTH && results.length === 0));
    if (!isDropdownVisible) {
      setDropdownMaxHeight(undefined);
      return;
    }

    const triggerEl = inputWrapperRef.current;
    if (!triggerEl) return;

    const triggerRect = triggerEl.getBoundingClientRect();

    // Same walk as the milestone date picker's calendarOpensUpward — find the
    // nearest ancestor that actually clips (not just the viewport), since a
    // scrollable modal body clips before the viewport edge ever matters.
    let scrollAncestor: HTMLElement | null = triggerEl.parentElement;
    while (scrollAncestor) {
      const overflowY = window.getComputedStyle(scrollAncestor).overflowY;
      if (overflowY === 'auto' || overflowY === 'scroll') break;
      scrollAncestor = scrollAncestor.parentElement;
    }
    const lowerBound = scrollAncestor
      ? Math.min(
          window.innerHeight,
          scrollAncestor.getBoundingClientRect().bottom
        )
      : window.innerHeight;

    // Small gap below the input (matches the CSS margin-top the dropdown
    // already uses) plus a little breathing room above the clipping edge.
    const DROPDOWN_GAP = 12;
    const availableBelow = lowerBound - triggerRect.bottom - DROPDOWN_GAP;

    // Only shrink when genuinely tight — otherwise leave it unset so the
    // CSS default (300px/400px depending on which stylesheet applies) wins,
    // matching today's look everywhere this isn't clipped.
    const DEFAULT_MAX_HEIGHT = 300;
    const MIN_USABLE_HEIGHT = 120;
    setDropdownMaxHeight(
      availableBelow < DEFAULT_MAX_HEIGHT
        ? Math.max(availableBelow, MIN_USABLE_HEIGHT)
        : undefined
    );
  }, [showResults, loading, results, inputValue]);

  // API search effect - supports both single (users) and dual (users + groups) API calls
  //
  // Debounced (300ms) and gated behind a 3-character minimum before firing any
  // network call — dual mode fires TWO requests per search (users + groups), so
  // without a debounce, fast typing was queuing up to 2 requests per keystroke,
  // most of them immediately superseded/discarded, competing with the one
  // request that actually matters for the browser's limited per-origin
  // connection pool. The 300ms delay collapses a burst of keystrokes into a
  // single search once typing actually pauses.
  useEffect(() => {
    let ignore = false;
    const trimmedInput = inputValue.trim();

    // Clear results if input is empty or below the minimum search length —
    // matches the "no search attempted yet" case, not "search found nothing"
    // (see the dropdown-visibility check below, which also gates on this).
    if (!trimmedInput || trimmedInput.length < MIN_QUERY_LENGTH) {
      setResults([]);
      setHasSelfMatch(false);
      setLoading(false);
      setApiError(null);
      return () => {
        ignore = true;
      };
    }

    if (!showResults) {
      return () => {
        ignore = true;
      };
    }

    // Drop stale rows and show the spinner immediately so typing still feels
    // responsive — only the actual network call is debounced below.
    setResults([]);
    setHasSelfMatch(false);
    setLoading(true);
    setApiError(null);

    const updateSearchResults = (users: UserIdentity[]) => {
      setHasSelfMatch(
        users.some(user =>
          isExcludedUser(user, [selfSelectionRef.current?.user])
        )
      );
      setResults(excludeUsers(users, excludeContactsRef.current));
    };

    const baseUrl = process.env.REACT_APP_API_V2_URL;
    if (!baseUrl) {
      setError(
        'API base URL is not configured. Please set REACT_APP_API_V2_URL in your environment.'
      );
      setLoading(false);
      return () => {
        ignore = true;
      };
    }

    const debounceTimer = setTimeout(() => {
      const requestId = ++searchRequestIdRef.current;

      const token = getCookie('app_access_token');
      const headers = {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      };

      // Determine which APIs to call based on searchMode
      const apiCalls: Promise<any>[] = [];

      // Always fetch users
      const usersUrl = `${baseUrl.replace(/\/$/, '')}/ldap/users/${inputValue}`;
      apiCalls.push(
        fetch(usersUrl, { headers })
          .then(res => {
            if (!res.ok) {
              if (res.status === 401) throw new Error('UNAUTHORIZED');
              if (res.status >= 500) throw new Error('SERVER_ERROR');
              throw new Error('NETWORK_ERROR');
            }
            return res.json();
          })
          .then(data => ({
            type: 'users',
            data: Array.isArray(data.data) ? data.data.slice(0, 50) : []
          }))
          .catch(error => ({ type: 'users', data: [], error: error.message }))
      );

      // If searchMode includes groups, also fetch groups
      if (searchMode === 'users-and-groups') {
        const groupsUrl = `${baseUrl.replace(
          /\/$/,
          ''
        )}/ldap/groups/${inputValue}`;
        apiCalls.push(
          fetch(groupsUrl, { headers })
            .then(res => {
              if (!res.ok) {
                if (res.status === 401) throw new Error('UNAUTHORIZED');
                if (res.status >= 500) throw new Error('SERVER_ERROR');
                throw new Error('NETWORK_ERROR');
              }
              return res.json();
            })
            .then(data => ({
              type: 'groups',
              data: Array.isArray(data.data) ? data.data.slice(0, 50) : []
            }))
            .catch(error => ({
              type: 'groups',
              data: [],
              error: error.message
            }))
        );
      }

      // Execute all API calls in parallel
      Promise.all(apiCalls)
        .then(responses => {
          if (ignore || requestId !== searchRequestIdRef.current) {
            return;
          }

          // Clear any previous API errors on successful fetch
          setApiError(null);

          // Combine results from both APIs
          let combinedResults: any[] = [];
          let hasAuthError = false;
          let hasServerError = false;
          let hasNetworkError = false;

          responses.forEach(response => {
            if (response.error) {
              // Track errors but don't immediately fail - other API might succeed
              if (response.error === 'UNAUTHORIZED') {
                hasAuthError = true;
              } else if (response.error === 'SERVER_ERROR') {
                hasServerError = true;
              } else {
                hasNetworkError = true;
              }
            } else {
              // Tag each result with its source type and combine
              const taggedResults = response.data.map((item: any) => ({
                ...item,
                email: item.email || item.mail,
                sourceType: response.type // 'users' or 'groups'
              }));
              combinedResults = [...combinedResults, ...taggedResults];
            }
          });

          // Only show error if ALL APIs failed or if there's an auth error
          if (hasAuthError) {
            setApiError({
              message:
                'Authentication expired. Please refresh the page to continue searching.',
              type: 'auth'
            });
          } else if (combinedResults.length === 0 && hasServerError) {
            setApiError({
              message:
                'LDAP service is currently unavailable. Please try again later.',
              type: 'network'
            });
          } else if (combinedResults.length === 0 && hasNetworkError) {
            setApiError({
              message:
                'Unable to search contacts. Please check your connection and try again.',
              type: 'general'
            });
          }

          updateSearchResults(combinedResults);

          // Keep focus on input field after results load
          setTimeout(focusInput, 0);

          // Fallback to mock data for development only if no results and no auth error
          if (
            process.env.NODE_ENV === 'development' &&
            combinedResults.length === 0 &&
            !hasAuthError
          ) {
            const mockResults = getMockResults(inputValue);
            if (requestId === searchRequestIdRef.current) {
              updateSearchResults(mockResults);
              setTimeout(focusInput, 0);
            }
          }
        })
        .finally(() => {
          if (!ignore && requestId === searchRequestIdRef.current) {
            setLoading(false);
            setTimeout(focusInput, 0);
          }
        });
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      ignore = true;
      clearTimeout(debounceTimer);
    };
  }, [inputValue, showResults, searchMode]);

  // Handle clicks outside of the component
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setShowResults(false);
        // Clear API errors when dropdown is closed
        setApiError(null);
      }
    }

    if (showResults) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showResults]);

  // Mock data for development/fallback
  const getMockResults = (searchTerm: string) => {
    const mockContacts = [
      {
        uid: 'asmith',
        name: 'Adam Smith',
        email: 'asmith@adobe.com',
        department: 'Marketing'
      },
      {
        uid: 'abrown',
        name: 'Alice Brown',
        email: 'abrown@adobe.com',
        department: 'Sales'
      },
      {
        uid: 'adavis',
        name: 'Andrew Davis',
        email: 'adavis@adobe.com',
        department: 'Engineering'
      },
      {
        uid: 'agarcia',
        name: 'Ana Garcia',
        email: 'agarcia@adobe.com',
        department: 'Customer Success'
      },
      {
        uid: 'agarwal',
        name: 'Aditya Agarwal',
        email: 'agarwal@adobe.com',
        department: 'Design'
      },
      {
        uid: 'aditi',
        name: 'Aditi Sharma',
        email: 'aditi@adobe.com',
        department: 'Marketing'
      },
      {
        uid: 'adisingh',
        name: 'Aditya Singh',
        email: 'adisingh@adobe.com',
        department: 'Engineering'
      },
      {
        uid: 'adk',
        name: 'Adi Kumar',
        email: 'adk@adobe.com',
        department: 'Engineering'
      },
      {
        uid: 'adpareek',
        name: 'Aditya Pareek',
        email: 'adpareek@adobe.com',
        department: 'Product'
      },
      {
        uid: 'adraghav',
        name: 'Aditya Raghuwanshi',
        email: 'adraghav@adobe.com',
        department: 'Engineering'
      },
      {
        uid: 'akag',
        name: 'ADITYA KUMAR AGRAWAL',
        email: 'akag@adobe.com',
        department: 'Engineering'
      },
      {
        uid: 'neha',
        name: 'Neha Patel',
        email: 'neha@adobe.com',
        department: 'Product'
      },
      {
        uid: 'nchopra',
        name: 'Neha Chopra',
        email: 'nchopra@adobe.com',
        department: 'Finance'
      },
      {
        uid: 'nsingh',
        name: 'Neha Singh',
        email: 'nsingh@adobe.com',
        department: 'HR'
      },
      {
        uid: 'nupadhyay',
        name: 'Neha Upadhyay',
        email: 'nupadhyay@adobe.com',
        department: 'Marketing'
      },
      {
        uid: 'abhiaditya',
        name: 'Abhinav Aditya',
        email: 'abhiaditya@adobe.com',
        department: 'Engineering'
      },
      {
        uid: 'pkoizumi',
        name: 'Peter Koizumi',
        email: 'pkoizumi@adobe.com',
        department: 'Design'
      },
      {
        uid: 'aditi.agrawal',
        name: 'Aaditi Agrawal',
        email: 'aditi.agrawal@adobe.com',
        department: 'Marketing'
      },
      {
        uid: 'aditomaso',
        name: 'Adam Ditomasso',
        email: 'aditomaso@adobe.com',
        department: 'Sales'
      }
    ];

    const searchTermLower = searchTerm.toLowerCase();
    return mockContacts.filter(
      contact =>
        contact.name.toLowerCase().includes(searchTermLower) ||
        contact.email.toLowerCase().includes(searchTermLower) ||
        contact.uid.toLowerCase().includes(searchTermLower)
    );
  };

  // Toggle contact selection (for checkbox functionality)
  const toggleContactSelection = (contact: any) => {
    if (isUserSelected(contact)) {
      // Remove from selection
      const updatedUsers = selectedUsers.filter(
        user =>
          !(
            (contact.uid && user.uid === contact.uid) ||
            (contact.name &&
              user.name?.toLowerCase() === contact.name?.toLowerCase())
          )
      );
      setSelectedUsers(updatedUsers);

      // Call onChange with the updated list, always as an array
      onChange(updatedUsers.length === 0 ? [] : updatedUsers);
    } else {
      if (isExcludedUser(contact, excludeContactsRef.current)) {
        return;
      }
      // Add to selection
      const updatedUsers = [...selectedUsers, contact];
      setSelectedUsers(updatedUsers);
      // Call onChange with the updated list, always as an array
      onChange(updatedUsers);
    }
  };

  // Add a new contact to the selected list (legacy method for backward compatibility)
  const handleSelectContact = (contact: any) => {
    if (isExcludedUser(contact, excludeContactsRef.current)) {
      return;
    }
    toggleContactSelection(contact);

    // Clear the input text after selection
    setInputValue('');

    // Close the picker and blur the field after selection
    setShowResults(false);
    setHighlightedIndex(-1);

    // Re-focus the input field so user can continue adding more contacts
    setTimeout(() => {
      focusInput();
    }, 0);
  };

  // Remove a contact from the selected list
  const handleRemoveContact = (contactToRemove: any) => {
    if (isChipRemoveDisabled?.(contactToRemove)) {
      return;
    }
    // Check if this is an existing contact that should be deleted
    if (contactToRemove.isExisting && onDeleteExistingContact) {
      console.log(
        '🗑️ Deleting existing DACI contact via chip X:',
        contactToRemove.name
      );
      onDeleteExistingContact(contactToRemove);
      // Don't call onChange here - the parent's onDeleteExistingContact handler
      // already updates the state, and calling onChange would cause a race condition
      // Re-focus the input after removal
      setTimeout(focusInput, 0);
      return; // Exit early for existing contacts
    }

    // For new (non-existing) contacts, remove from local list
    const updatedUsers = selectedUsers.filter(
      user => user.uid !== contactToRemove.uid
    );
    setSelectedUsers(updatedUsers);

    // Call onChange with the updated list, always as an array
    onChange(updatedUsers.length === 0 ? [] : updatedUsers);

    // Re-focus the input after removal
    setTimeout(focusInput, 0);
  };

  // Helper function to get CSS class (use provided or fall back to default)
  const getClass = (
    className: keyof Required<LDAPSearchFieldProps>['cssClasses']
  ) => {
    return cssClasses?.[className] || styles[className];
  };

  return (
    <div
      ref={containerRef}
      className={getClass('ldapFieldContainer') || styles.ldapFieldContainer}
      data-testid="ldap-field-container"
    >
      <div
        ref={inputWrapperRef}
        className={`${getClass('pickerContainer') || styles.pickerContainer} ${
          isInvalid ? styles.invalid : ''
        }`}
        onClick={() => {
          setShowResults(true);
          focusInput();
        }}
        onKeyDown={e => {
          const targetEl = e.target as HTMLElement;
          const isInputEl =
            targetEl.tagName === 'INPUT' || targetEl === inputFieldRef.current;

          // Allow typing spaces in the input field
          if ((e.key === ' ' || e.key === 'Spacebar') && isInputEl) {
            return;
          }

          // Only toggle dropdown on Enter/Space when focus is on the container (not the input)
          if ((e.key === 'Enter' || e.key === ' ') && !isInputEl) {
            e.preventDefault();
            setShowResults(prev => !prev);
            focusInput();
          }
          if (e.key === 'ArrowDown' && !showResults) {
            e.preventDefault();
            setShowResults(true);
            focusInput();
            setHighlightedIndex(0);
          } else if (e.key === 'ArrowDown' && showResults) {
            e.preventDefault();
            setHighlightedIndex(prev => {
              const next = Math.min(
                (prev < 0 ? -1 : prev) + 1,
                results.length - 1
              );
              requestAnimationFrame(() => {
                const el = resultsListRef.current?.querySelector(
                  `[data-ldap-index="${next}"]`
                ) as HTMLElement | null;
                el?.scrollIntoView({ block: 'nearest' });
              });
              return next;
            });
          } else if (e.key === 'ArrowUp' && showResults) {
            e.preventDefault();
            setHighlightedIndex(prev => {
              const next = Math.max((prev < 0 ? 0 : prev) - 1, 0);
              requestAnimationFrame(() => {
                const el = resultsListRef.current?.querySelector(
                  `[data-ldap-index="${next}"]`
                ) as HTMLElement | null;
                el?.scrollIntoView({ block: 'nearest' });
              });
              return next;
            });
          } else if (e.key === 'Escape' && showResults) {
            e.preventDefault();
            setShowResults(false);
            setHighlightedIndex(-1);
          } else if (
            e.key === 'Enter' &&
            showResults &&
            highlightedIndex >= 0
          ) {
            e.preventDefault();
            const user = results[highlightedIndex];
            if (user && !isUserAlreadyInRole(user)) {
              handleSelectContact(user);
            }
          }
        }}
        data-testid="ldap-picker-container"
        tabIndex={0}
      >
        <input
          ref={inputFieldRef}
          type="text"
          className={styles.customTextField}
          value={inputValue}
          onMouseDown={e => e.stopPropagation()}
          onClick={e => {
            e.stopPropagation();
            setShowResults(true);
          }}
          onChange={e => {
            setInputValue(e.target.value);
            if (e.target.value) {
              setShowResults(true);
            }
          }}
          placeholder="Start Typing for Users"
          data-testid="ldap-search-input"
        />
        <div
          className={styles.chevronContainer}
          data-testid="ldap-chevron-container"
        >
          {isInvalid && <Alert UNSAFE_className={styles.errorIcon} />}
          <ChevronDown />
        </div>
      </div>

      {/* Selected contact tags displayed below the input field */}
      {selectedUsers.length > 0 && (
        <div
          className={getClass('tagContainer')}
          data-testid="ldap-selected-tags-container"
        >
          {selectedUsers.map(user => {
            const removalLocked = Boolean(isChipRemoveDisabled?.(user));
            const tagClass = [
              getClass('selectedTag'),
              removalLocked ? styles.selectedTagRemoveDisabled : ''
            ]
              .filter(Boolean)
              .join(' ');
            return (
              <div
                key={user.uid}
                className={tagClass}
                title={removalLocked ? chipRemoveDisabledTitle : undefined}
                aria-label={
                  removalLocked
                    ? `${user.name}, ${chipRemoveDisabledTitle}`
                    : undefined
                }
                data-testid={`ldap-selected-tag-${user.uid}`}
              >
                <span>{user.name}</span>
                {!removalLocked && (
                  <button
                    type="button"
                    className={styles.tagRemoveButton}
                    onMouseDown={e => {
                      // Use onMouseDown instead of onClick for immediate response
                      e.stopPropagation();
                      e.preventDefault();
                      handleRemoveContact(user);
                    }}
                    onClick={e => {
                      e.stopPropagation();
                      e.preventDefault();
                    }}
                    aria-label={`Remove ${user.name}`}
                    data-testid={`ldap-remove-tag-${user.uid}`}
                  >
                    <span
                      className={styles['safari-icon-fix']}
                      style={
                        {
                          '--spectrum-icon-size-s': '11px'
                        } as React.CSSProperties
                      }
                    >
                      <Close size="S" />
                    </span>
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Search results dropdown - only show when loading or has results */}
      {showResults &&
        (loading ||
          results.length > 0 ||
          (inputValue.trim().length >= MIN_QUERY_LENGTH &&
            results.length === 0)) && (
          <div
            ref={dropdownRef}
            className={getClass('ldapDropdown')}
            data-testid="ldap-search-results-dropdown"
            style={
              dropdownMaxHeight !== undefined
                ? { maxHeight: dropdownMaxHeight }
                : undefined
            }
          >
            {loading ? (
              <div
                className={styles.loadingResults}
                data-testid="ldap-search-loading"
              >
                <div
                  className={styles.customSpinner}
                  data-testid="ldap-search-loading-spinner"
                ></div>
                <Text>Searching...</Text>
              </div>
            ) : results.length > 0 ? (
              <div
                ref={resultsListRef}
                className={styles.searchResultsList}
                data-testid="ldap-search-results-list"
              >
                {results.map((user, idx) => {
                  const isInTable = isUserAlreadyInRole(user);
                  const isSelected = isUserSelected(user);
                  const itemKey = resultKey(user, idx);

                  return (
                    <div
                      key={itemKey}
                      className={`${styles.searchResultItem} ${
                        isSelected ? styles.selected : ''
                      } ${isInTable ? styles.inTable : ''} ${
                        !isSelected && !isInTable && highlightedIndex === idx
                          ? styles.highlighted
                          : ''
                      }`}
                      onClick={() => !isInTable && handleSelectContact(user)}
                      data-testid={`ldap-search-result-${itemKey}`}
                      data-ldap-index={idx}
                    >
                      <div
                        className={styles.userInfo}
                        data-testid={`ldap-result-userinfo-${itemKey}`}
                      >
                        <div
                          className={styles.userName}
                          data-testid={`ldap-result-username-${itemKey}`}
                        >
                          {formatResultLabel(user)}
                        </div>
                        {user.department && (
                          <div
                            className={styles.userDepartment}
                            data-testid={`ldap-result-userdepartment-${itemKey}`}
                          >
                            {user.department}
                          </div>
                        )}
                      </div>

                      <div
                        className={styles.checkboxContainer}
                        data-testid={`ldap-result-checkbox-container-${itemKey}`}
                      >
                        {(isInTable || isSelected) && (
                          <BlackCheckMarkIcon
                            width={18}
                            height={16}
                            data-testid={`ldap-result-already-added-${itemKey}`}
                          />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              // Only show "No matching users found" when user has typed something
              inputValue && (
                <div className={styles.noResults} data-testid="ldap-no-results">
                  <Text>
                    {hasSelfMatch && selfSelection
                      ? selfSelection.message
                      : 'No matching users found'}
                  </Text>
                </div>
              )
            )}
          </div>
        )}

      {/* API Error Banner */}
      {apiError && (
        <div style={{ marginTop: '8px' }}>
          <AlertBanner
            variant="error"
            message={apiError.message}
            onClose={() => setApiError(null)}
          />
        </div>
      )}
    </div>
  );
};

export default LDAPSearchField;
